/**
 * Auth repository (infrastructure).
 * Talks to /api/v1/auth/login/, /logout/, /profile/.
 */
import type { AxiosInstance } from 'axios';

import type { AdminUser } from '@domain/auth/AdminUser';
import { ADMIN_API_BASE, AUTH_API_BASE } from '@infrastructure/api/client';

export class AuthRepository {
  constructor(private readonly api: AxiosInstance) {}

  /** Step 1: the password. Staff always get a second step (no session yet). */
  async login(email: string, password: string): Promise<{ mfaToken: string }> {
    const r = await this.api.post(`${AUTH_API_BASE}/login/`, { email, password });
    if (r.data?.mfa_required && typeof r.data.mfa_token === 'string') return { mfaToken: r.data.mfa_token };
    throw new Error('Unexpected sign-in reply. Reload the page and try again.');
  }

  /** Step 2: authenticator or recovery code; the server sets the session cookies. */
  async verifyCode(mfaToken: string, code: string): Promise<AdminUser> {
    await this.api.post(`${AUTH_API_BASE}/login/mfa/`, { mfa_token: mfaToken, code });
    return this.me();
  }

  async logout(): Promise<void> {
    await this.api.post(`${AUTH_API_BASE}/logout/`);
  }

  async me(): Promise<AdminUser> {
    // Use the admin-portal-specific identity endpoint — it includes the
    // is_staff/is_admin/groups fields the routing state machine needs.
    const r = await this.api.get(`${ADMIN_API_BASE}/whoami/`);
    const u = r.data;
    return {
      id: String(u.id),
      email: u.email,
      is_staff: Boolean(u.is_staff),
      is_admin: Boolean(u.is_admin),
      is_superuser: Boolean(u.is_superuser),
      groups: Array.isArray(u.groups)
        ? u.groups.map((g: { name?: string } | string) => (typeof g === 'string' ? g : g.name ?? ''))
        : [],
    };
  }
}
